'use client';

import React from 'react';

interface AIReviewSummaryProps {
  aiSummary: string | null;
  loading: boolean;
  ratingDistribution: Record<number, number>;
  totalReviews: number;
}

/**
 * Compute sentiment counts from star-rating distribution.
 * Positive = 4-5 stars, Neutral = 3, Negative = 1-2.
 */
function computeSentiment(dist: Record<number, number>) {
  const positive = (Number(dist[4]) || 0) + (Number(dist[5]) || 0);
  const neutral = Number(dist[3]) || 0;
  const negative = (Number(dist[1]) || 0) + (Number(dist[2]) || 0);
  const total = positive + neutral + negative;
  return { positive, neutral, negative, total };
}

const AIReviewSummary: React.FC<AIReviewSummaryProps> = ({
  aiSummary,
  loading,
  ratingDistribution,
  totalReviews,
}) => {
  const sentiment = computeSentiment(ratingDistribution);

  // Don't render if no reviews at all
  if (totalReviews === 0 && !aiSummary) return null;

  // Show loading skeleton while AI summary is being fetched
  if (loading) {
    return (
      <div className="mt-6 rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-gray-50 p-5 animate-pulse">
        <div className="h-4 w-48 bg-gray-200 rounded mb-4" />
        <div className="space-y-2.5">
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-5/6" />
          <div className="h-3 bg-gray-200 rounded w-4/6" />
        </div>
        <div className="mt-4 h-3 bg-gray-200 rounded-full w-full" />
      </div>
    );
  }

  // If no AI summary and we have sentiment data, just show the sentiment bar
  const hasAISummary = aiSummary && aiSummary.trim().length > 0;
  const hasSentiment = sentiment.total > 0;

  if (!hasAISummary && !hasSentiment) return null;

  return (
    <div className="mt-6 rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-gray-50 p-5">
      {/* Header */}
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <span className="text-base">&#10024;</span>
        What campers are saying
      </h3>

      {/* AI Summary Text */}
      {hasAISummary && (
        <p className="text-sm text-gray-700 leading-relaxed mb-4">
          {aiSummary}
        </p>
      )}

      {/* Sentiment Bar */}
      {hasSentiment && (
        <div>
          <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-200">
            {sentiment.positive > 0 && (
              <div
                className="bg-green-500 transition-all duration-700"
                style={{ width: `${(sentiment.positive / sentiment.total) * 100}%` }}
                title={`${sentiment.positive} positive`}
              />
            )}
            {sentiment.neutral > 0 && (
              <div
                className="bg-gray-400 transition-all duration-700"
                style={{ width: `${(sentiment.neutral / sentiment.total) * 100}%` }}
                title={`${sentiment.neutral} neutral`}
              />
            )}
            {sentiment.negative > 0 && (
              <div
                className="bg-red-500 transition-all duration-700"
                style={{ width: `${(sentiment.negative / sentiment.total) * 100}%` }}
                title={`${sentiment.negative} negative`}
              />
            )}
          </div>

          {/* Sentiment labels */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {sentiment.positive > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                {sentiment.positive} positive
              </span>
            )}
            {sentiment.neutral > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                {sentiment.neutral} neutral
              </span>
            )}
            {sentiment.negative > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                {sentiment.negative} negative
              </span>
            )}
          </div>
        </div>
      )}

      {/* AI Disclaimer */}
      {hasAISummary && (
        <p className="mt-3 text-[11px] text-gray-400 italic flex items-center gap-1">
          <span className="text-[10px]">&#9432;</span>
          AI-generated from user review text
        </p>
      )}
    </div>
  );
};

export default AIReviewSummary;
